import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { apiService } from "../../services/axios";

export const fetchDrawingQuestions = createAsyncThunk(
  "drawingQuestion/fetchDrawingQuestions",
  async ({
    page = 1,
    limit = 25,
    categoryId,
    subcategoryId,
    searchTerm,
    courseId,
    status,
    previousExamRefId,
  } = {}) => {
    let url = "drawing-question/getAll?";
    if (categoryId) {
      const categoryParam = Array.isArray(categoryId) ? categoryId.join(",") : categoryId;
      if (categoryParam) url += `&categoryId=${categoryParam}`;
    }
    if (subcategoryId) {
      const subCategoryParam = Array.isArray(subcategoryId) ? subcategoryId.join(",") : subcategoryId;
      if (subCategoryParam) url += `&subCategoryId=${subCategoryParam}`;
    }
    if (courseId) {
      const courseParam = Array.isArray(courseId) ? courseId.join(",") : courseId;
      if (courseParam) url += `&courseId=${courseParam}`;
    }
    if (previousExamRefId) {
      const examRefParam = Array.isArray(previousExamRefId) ? previousExamRefId.join(",") : previousExamRefId;
      if (examRefParam) url += `&previousExamRefId=${examRefParam}`;
    }
    if (searchTerm) url += `&searchTerm=${searchTerm}`;
    if (status && status !== "all") url += `&status=${status}`;
    const response = await apiService.get(
      url + "&page=" + page + "&limit=" + limit
    );
    return response;
  }
);

export const toggleDrawingQuestionStatus = createAsyncThunk(
  "drawingQuestion/toggleStatus",
  async ({ _id, isActive }) => {
    const body = { questionId: _id, isActive };
    const response = await apiService.put("/drawing-question/updateStatus", body);
    return response.data?.data || { _id, isActive };
  }
);

export const addDrawingQuestion = createAsyncThunk(
  "drawingQuestion/add",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await apiService.post("/drawing-question/add", payload);
      return response.data || response;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Failed to add drawing question" });
    }
  }
);

export const updateDrawingQuestion = createAsyncThunk(
  "drawingQuestion/update",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await apiService.put("/drawing-question/update", payload);
      return response.data || response;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Failed to update drawing question" });
    }
  }
);

export const deleteDrawingQuestion = createAsyncThunk(
  "drawingQuestion/delete",
  async (questionId, { rejectWithValue }) => {
    try {
      await apiService.put("/drawing-question/delete", {
        questionId,
        isDelete: true,
      });
      return questionId;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Failed to delete drawing question" });
    }
  }
);

const drawingQuestionSlice = createSlice({
  name: "drawingQuestion",
  initialState: {
    questions: [],
    totalQuestions: 0,
    loading: false,
    error: null,
  },
  reducers: {
    clearDrawingQuestions: (state) => {
      state.questions = [];
      state.totalQuestions = 0;
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDrawingQuestions.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchDrawingQuestions.fulfilled, (state, action) => {
        state.loading = false;
        state.questions = action.payload?.questions || [];
        state.totalQuestions = action.payload?.pagination?.total || 0;
      })
      .addCase(fetchDrawingQuestions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(toggleDrawingQuestionStatus.fulfilled, (state, action) => {
        const updated = action.payload;
        const index = state.questions.findIndex((q) => q._id === updated._id);
        if (index !== -1) {
          state.questions[index].isActive = updated.isActive;
        }
      })
      .addCase(addDrawingQuestion.fulfilled, (state, action) => {
        if (action.payload?.data) {
          state.questions.unshift(action.payload.data);
        }
      })
      .addCase(updateDrawingQuestion.fulfilled, (state, action) => {
        const updated = action.payload?.data || action.payload;
        const index = state.questions.findIndex((q) => q._id === updated._id);
        if (index !== -1) {
          state.questions[index] = updated;
        }
      })
      .addCase(deleteDrawingQuestion.fulfilled, (state, action) => {
        state.questions = state.questions.filter(
          (q) => q._id !== action.payload
        );
      });
  },
});

export const { clearDrawingQuestions } = drawingQuestionSlice.actions;
export default drawingQuestionSlice.reducer;

